import { useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/bakery/PageHero";
import { ChartArt } from "@/components/bakery/Art";
import Lightbox from "@/components/common/Lightbox";
import { inputCls } from "@/components/common/Field";
import { Card, Loading } from "@/components/common/ui";
import { useApi } from "@/lib/useApi";
import { fileUrl } from "@/lib/api";
import { CHANNELS, SALES_TYPES, fmtDate, shiftDate, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

const PERIODS = [["harian", "Harian"], ["mingguan", "Mingguan"], ["bulanan", "Bulanan"]];

function range(period, a) {
  if (period === "harian") return [a, a];
  const d = new Date(`${a}T00:00:00Z`);
  if (period === "mingguan") {
    const mon = shiftDate(a, -((d.getUTCDay() + 6) % 7));
    return [mon, shiftDate(mon, 6)];
  }
  const first = `${a.slice(0, 7)}-01`;
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
  return [first, last];
}

const Section = ({ title, children, testid }) => (
  <Card className="overflow-hidden p-0" data-testid={testid}>
    <div className="border-b-2 border-ink px-5 py-3 font-display text-lg font-bold">{title}</div>
    <div className="overflow-x-auto">{children}</div>
  </Card>
);

function SalesTable({ rows }) {
  const tot = (fn) => rows.reduce((s, r) => s + fn(r), 0);
  return (
    <table className="dense-table w-full min-w-[860px]">
      <thead><tr><th>Produk</th>{CHANNELS.map((c) => <th key={c} className="text-right">{c}</th>)}{SALES_TYPES.map((t) => <th key={t} className="text-right">{t}</th>)}<th className="text-right">Total</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.product_id}><td className="font-medium">{r.name}</td>
            {CHANNELS.map((c) => <td key={c} className="num text-right">{r.channels[c] || ""}</td>)}
            {SALES_TYPES.map((t) => <td key={t} className="num text-right">{r.types[t] || ""}</td>)}
            <td className="num text-right font-bold">{r.total}</td></tr>
        ))}
        <tr className="bg-zinc-100 font-bold"><td>Total</td>
          {CHANNELS.map((c) => <td key={c} className="num text-right">{tot((r) => r.channels[c] || 0)}</td>)}
          {SALES_TYPES.map((t) => <td key={t} className="num text-right">{tot((r) => r.types[t] || 0)}</td>)}
          <td className="num text-right">{tot((r) => r.total)}</td></tr>
      </tbody>
    </table>
  );
}

function DailyText({ date }) {
  const { data, loading } = useApi("/reports/daily", { date });
  if (loading && !data) return <Loading />;
  return (
    <div className="p-5">
      <button onClick={() => { navigator.clipboard?.writeText(data?.text || ""); toast.success("Teks laporan disalin"); }}
        className="press mb-3 inline-flex items-center gap-2 rounded-md border-2 border-ink px-3 py-2 text-xs font-semibold" data-testid="copy-report-text"><Copy className="h-3.5 w-3.5" /> Salin teks</button>
      <pre className="num whitespace-pre-wrap rounded-lg bg-ink p-4 text-xs leading-relaxed text-zinc-100" data-testid="daily-report-text">{data?.text}</pre>
    </div>
  );
}

export default function Laporan() {
  const [period, setPeriod] = useState("harian");
  const [anchor, setAnchor] = useState(shiftDate(todayJkt(), -1));
  const [f, t] = range(period, anchor);
  const { data, loading } = useApi("/laporan", { date_from: f, date_to: t });
  const [lb, setLb] = useState({ photos: [], index: null });
  return (
    <div data-testid="laporan-page">
      <PageHero testid="laporan-hero" eyebrow="Admin · Laporan" title="Laporan" Art={ChartArt} compact desc="Penjualan per channel & tipe, waste per alasan, produksi per batch, dan saldo Frozen Dough." />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex rounded-md border-2 border-ink bg-white p-1">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`} className={cn("press rounded px-4 py-2 text-sm font-semibold", period === k ? "bg-ink text-white" : "")}>{l}</button>
          ))}
        </div>
        <input type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} className={cn(inputCls, "sm:w-48")} data-testid="laporan-date" />
        <span className="text-sm text-zinc-600" data-testid="laporan-range">{fmtDate(f)}{f !== t && ` — ${fmtDate(t)}`}</span>
      </div>
      {loading && !data ? <Loading /> : data && (
        <div className="space-y-6">
          <Section title="Penjualan · produk × channel × tipe" testid="laporan-sales">{data.sales.length ? <SalesTable rows={data.sales} /> : <p className="p-5 text-sm text-zinc-500">Belum ada penjualan.</p>}</Section>
          <div className="grid gap-6 xl:grid-cols-2">
            <Section title="Waste · produk × alasan" testid="laporan-waste">
              <table className="dense-table w-full"><thead><tr><th>Produk</th><th>Alasan</th><th className="text-right">Qty</th><th className="text-right">Entri</th></tr></thead>
                <tbody>{data.waste.map((r, i) => <tr key={i}><td>{r.name}</td><td>{r.reason}</td><td className="num text-right font-bold text-brand">{r.qty}</td><td className="num text-right">{r.entries}</td></tr>)}</tbody></table>
              {data.waste_entries.some((w) => w.photos.length) && (
                <div className="flex flex-wrap gap-2 border-t p-4">
                  {data.waste_entries.flatMap((w) => w.photos.map((p, i) => (
                    <button key={p.id} onClick={() => setLb({ photos: w.photos, index: i })} className="h-14 w-14 overflow-hidden rounded border-2 border-ink" title={`${w.product} · ${w.reason}`} data-testid="laporan-waste-photo">
                      <img src={fileUrl(p.id)} alt="waste" className="h-full w-full object-cover" />
                    </button>
                  )))}
                </div>
              )}
            </Section>
            <Section title="Saldo Frozen Dough (saat ini)" testid="laporan-fd">
              <table className="dense-table w-full"><thead><tr><th>Item FD</th><th className="text-right">Stok</th></tr></thead>
                <tbody>{data.fd_balance.map((r) => <tr key={r.product_id}><td>{r.name}</td><td className={cn("num text-right font-bold", r.stock < 0 && "text-brand", r.stock === 0 && "text-zinc-300")}>{r.stock}</td></tr>)}</tbody></table>
            </Section>
          </div>
          <Section title="Produksi · per varian" testid="laporan-production">
            <table className="dense-table w-full"><thead><tr><th>Varian</th><th className="text-right">Roti Jadi</th><th className="text-right">Frozen Dough</th><th className="text-right">Panggang FD</th></tr></thead>
              <tbody>{data.production.variants.map((r) => <tr key={r.product_id}><td>{r.name}</td><td className="num text-right">{r.roti}</td><td className="num text-right">{r.fd}</td><td className="num text-right">{r.bake}</td></tr>)}</tbody></table>
            {data.production.batches.length > 0 && (
              <div className="grid gap-2 border-t p-4 sm:grid-cols-2 lg:grid-cols-3">
                {data.production.batches.map((b) => (
                  <div key={b.txn_id} className="rounded-md border border-zinc-200 p-3 text-xs"><div className="font-semibold">{fmtDate(b.date)} · Batch {b.batch_no} <span className="num text-zinc-400">{b.txn_no}</span></div>
                    <div className="mt-1 text-zinc-600">{b.lines.map((l) => `${l.name} ×${l.qty}${l.output === "fd" ? " (FD)" : ""}`).join(", ")}</div></div>
                ))}
              </div>
            )}
          </Section>
          {period === "harian" && <Section title={`Teks Laporan Harian ${fmtDate(anchor)}`} testid="laporan-daily-text"><DailyText date={anchor} /></Section>}
        </div>
      )}
      <Lightbox photos={lb.photos} index={lb.index} onIndex={(i) => setLb({ ...lb, index: i })} onClose={() => setLb({ photos: [], index: null })} />
    </div>
  );
}
